"use client";

import * as React from "react";
import { BottomSheet } from "./bottom-sheet";
import { SHARE_SHEET_CLASS_NAME } from "./bottom-sheet.constants";
import { cn } from "../../lib/utils";
import { ShareSettingsContent } from "./share-settings-content";
import { ShareOverview } from "./share-overview";
import { ShareInstructionsContent } from "./share-instructions-content";
import { getPlatformInstructions } from "./share-platform-instructions";
import { Toast, useToast } from "./toast";

import type {
  ShareBottomSheetProps,
  ShareOptionInstructions,
  ShareOption,
  LinkedPlatform,
} from "../sharing/sharing.types";
export type {
  ShareBottomSheetProps,
  ShareOptionInstructions,
  ShareInstructionConfirmationAction,
  ShareDirectInstruction,
  ShareOption,
  LinkedPlatform,
} from "../sharing/sharing.types";

export function ShareBottomSheet({
  isOpen,
  onClose,
  mode = "profile",
  jobData,
  profileName,
  profileHandle,
  profileAvatar,
  shareLink,
  linkedPlatforms = [],
  shareOptions = [],
  onCopyLink,
  copyDisabled,
  copyErrorMessage,
  onEditProfile,
  className,
  title,
  subtitle,
  hideEditButton = false,
  hideSettings = false,
  themeColor = "#FFF8E8",
  onThemeColorChange,
  previewCard,
  directInstruction,
  instructionConfirmationAction,
  platformsOnly = false,
  tipNotice,
  belowShareLink,
}: ShareBottomSheetProps) {
  const [view, setView] = React.useState<"share" | "settings" | "instructions">(
    directInstruction ? "instructions" : "share",
  );
  const [currentThemeColor, setCurrentThemeColor] = React.useState(themeColor);
  const [activeInstruction, setActiveInstruction] = React.useState<{
    icon?: React.ReactNode;
    instructions: ShareOptionInstructions;
  } | null>(directInstruction ?? null);

  const { toast, showToast, hideToast } = useToast();

  // Use centralized platform instructions
  const getDefaultPlatformInstructions = React.useCallback(
    (platformId: string, profileUrl?: string) => {
      return getPlatformInstructions(platformId, shareLink, profileUrl);
    },
    [shareLink],
  );

  React.useEffect(() => {
    if (isOpen) {
      setView(directInstruction ? "instructions" : "share");
      setActiveInstruction(directInstruction ?? null);
    }
  }, [directInstruction, isOpen]);

  React.useEffect(() => {
    setCurrentThemeColor(themeColor);
  }, [themeColor]);

  const handleCopyLink = React.useCallback(async () => {
    if (copyDisabled) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      onCopyLink?.();
      showToast("Link copied to clipboard!");
    } catch {
      if (copyErrorMessage) showToast(copyErrorMessage);
    }
  }, [copyDisabled, copyErrorMessage, onCopyLink, shareLink, showToast]);

  const handleThemeColorChange = React.useCallback(
    (color: string) => {
      setCurrentThemeColor(color);
      onThemeColorChange?.(color);
    },
    [onThemeColorChange],
  );

  const handleShareOptionSelect = (option: ShareOption) => {
    // Get instructions from option or fetch default instructions
    const instructionsPayload =
      option.instructions ??
      getDefaultPlatformInstructions(option.id, option.profileUrl);

    if (instructionsPayload) {
      setActiveInstruction({
        icon: option.icon,
        instructions: instructionsPayload,
      });
      setView("instructions");
    }
    option.onClick?.();
  };

  const handlePlatformSelect = (platform: LinkedPlatform) => {
    const instructionsPayload =
      platform.instructions ??
      getDefaultPlatformInstructions(platform.id, platform.profileUrl);

    if (instructionsPayload) {
      setActiveInstruction({
        icon: platform.icon,
        instructions: instructionsPayload,
      });
      setView("instructions");
    }
    platform.onClick?.();
  };

  const handleEditSettings = () => {
    if (!hideSettings) {
      setView("settings");
      onEditProfile?.();
    }
  };

  const activeConfirmationAction =
    directInstruction?.confirmationAction ?? instructionConfirmationAction;

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        // title={sheetTitle}
        className={cn(SHARE_SHEET_CLASS_NAME, className)}
      >
        <div className="relative">
          {view === "share" && (
            <ShareOverview
              mode={mode}
              jobData={jobData}
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              shareLink={shareLink}
              themeColor={currentThemeColor}
              linkedPlatforms={linkedPlatforms}
              shareOptions={shareOptions}
              onCopyLink={handleCopyLink}
              copyDisabled={copyDisabled}
              onEditSettings={hideSettings ? undefined : handleEditSettings}
              onLinkedPlatformSelect={handlePlatformSelect}
              onShareOptionSelect={handleShareOptionSelect}
              hideEditButton={hideEditButton || hideSettings}
              previewCard={previewCard}
              platformsOnly={platformsOnly}
              title={title}
              subtitle={subtitle}
              tipNotice={tipNotice}
              belowShareLink={belowShareLink}
            />
          )}
          {view === "settings" && !hideSettings && (
            <ShareSettingsContent
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              themeColor={currentThemeColor}
              setThemeColor={handleThemeColorChange}
              onBack={() => setView("share")}
            />
          )}
          {view === "instructions" && activeInstruction && (
            <ShareInstructionsContent
              profileName={profileName}
              profileHandle={profileHandle}
              profileAvatar={profileAvatar}
              shareLink={shareLink}
              icon={activeInstruction.icon}
              instructions={activeInstruction.instructions}
              onBack={() => {
                if (directInstruction) {
                  onClose();
                  return;
                }
                setActiveInstruction(null);
                setView("share");
              }}
              onCopyLink={handleCopyLink}
              onEditSettings={hideSettings ? undefined : handleEditSettings}
              onShowToast={showToast}
              confirmationAction={
                activeConfirmationAction
                  ? {
                      label: activeConfirmationAction.label,
                      onClick: () => {
                        activeConfirmationAction.onClick?.();
                        onClose();
                      },
                    }
                  : undefined
              }
            />
          )}
        </div>
      </BottomSheet>

      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </>
  );
}
